import React, { useState, useMemo } from 'react';
import { Sun, Leaf, Calendar } from 'lucide-react';
import Hero from '@/components/Hero';
import SectionHeader from '@/components/SectionHeader';
import Reveal from '@/components/Reveal';
import MangoIllustration from '@/components/MangoIllustration';
import SquareButton from '@/components/SquareButton';
import SeasonalCalendar from '@/components/SeasonalCalendar';
import Seo from '@/components/Seo';
import { STOCK } from '@/lib/constants';

const VARIETIES = [
  { slug: 'tommy-atkins', name: 'Tommy Atkins', flavor: 'Mild and sweet with a firm, fibrous flesh. The most widely available Florida variety.', ripening: 'Skin turns from green to red-blushed with yellow undertones. Yields slightly to gentle pressure.', season: 'June – August', availability: 'Peak Season', badge: 'peak', color: 'red' },
  { slug: 'haden', name: 'Haden', flavor: 'Rich, aromatic, with hints of peach and citrus. The original South Florida heritage mango.', ripening: 'Bright red shoulders fading to golden yellow. Strong floral fragrance when ripe.', season: 'May – July', availability: 'Peak Season', badge: 'peak', color: 'orange' },
  { slug: 'keitt', name: 'Keitt', flavor: 'Sweet, fiberless, and creamy. Excellent for slicing and grilling.', ripening: 'Stays green even when ripe — trust the gentle give of the flesh, not the color.', season: 'August – October', availability: 'Late Season', badge: 'limited', color: 'leaf' },
  { slug: 'kent', name: 'Kent', flavor: 'Juicy, sweet, and almost fiberless. A favorite for fresh eating and smoothies.', ripening: 'Green-yellow skin with a red blush. Heavier feel signals ripeness.', season: 'July – September', availability: 'In Season', badge: 'seasonal', color: 'yellow' },
  { slug: 'valencia-pride', name: 'Valencia Pride', flavor: 'Sweet, tender, with a delicate aroma. Elongated, elegant shape.', ripening: 'Bright yellow with rose blush. Releases sweet aroma at stem end when ripe.', season: 'July – September', availability: 'In Season', badge: 'seasonal', color: 'orange' },
  { slug: 'carrie', name: 'Carrie', flavor: 'Intensely sweet, rich, with a slight piney note. A South Florida cult favorite.', ripening: 'Stays green; soft give and heady fragrance tell you it’s ready.', season: 'June – July', availability: 'Limited Harvest', badge: 'limited', color: 'leaf' },
  { slug: 'growers-choice-mix', name: "Grower's Choice Mix", flavor: 'A curated mix of whatever is peaking on local trees that week — the freshest possible selection.', ripening: 'Includes a ripening guide tailored to the varieties in your box.', season: 'May – October', availability: 'Always Available', badge: 'seasonal', color: 'teal' },
];

const COLOR_BG = {
  red: 'bg-[hsl(var(--c-mango-red))]',
  orange: 'bg-[hsl(var(--c-mango-orange))]',
  yellow: 'bg-[hsl(var(--c-mango-yellow))]',
  leaf: 'bg-[hsl(var(--c-leaf))]',
  teal: 'bg-[hsl(var(--c-teal))]',
};

const BADGE_BG = {
  peak: 'bg-[hsl(var(--c-leaf))] text-white',
  seasonal: 'bg-[hsl(var(--c-mango-yellow))] text-[hsl(var(--c-ink))]',
  limited: 'bg-[hsl(var(--c-mango-red))] text-white',
};

export default function Varieties() {
  const [filter, setFilter] = useState('all');
  const filtered = useMemo(() => {
    if (filter === 'all') return VARIETIES;
    return VARIETIES.filter((v) => v.badge === filter);
  }, [filter]);

  return (
    <div data-testid="varieties-page">
      <Seo
        title="Florida Mango Varieties"
        description="Explore South Florida mango varieties: Tommy Atkins, Haden, Keitt, Kent, Valencia Pride, Carrie, and Grower’s Choice Mix. Flavor profiles, ripening guides, and harvest seasons."
        keywords="mango varieties, Tommy Atkins, Haden, Keitt, Kent, Valencia Pride, Carrie mango, mango season Florida"
        ogImage={STOCK.variety_array}
      />

      <Hero
        testId="varieties-hero"
        eyebrow="Florida Mango Varieties"
        title={<>The flavors <br className="hidden sm:block" /> of <span className="text-[hsl(var(--c-mango-yellow))]">South Florida</span>.</>}
        subtitle="Florida’s climate produces some of the most complex, aromatic mangoes in the world. Each variety has its own season, sweetness, texture, and ripening signal."
        block="leaf"
        bgImage={STOCK.mango_tree}
        rightImage={STOCK.variety_array}
        imageBlock="yellow"
        rightAccent="red"
        leftAccent="orange"
        ctas={[
          { label: 'See All Varieties', href: '#varieties-grid', variant: 'yellow', testId: 'varieties-hero-see-all' },
          { label: 'Shop A Box', to: '/shop', variant: 'orange', testId: 'varieties-hero-shop' },
        ]}
      />

      {/* Calendar */}
      <section className="block-cream-deep border-b-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-16 lg:py-24">
          <Reveal>
            <SectionHeader
              eyebrow="Florida Mango Calendar"
              size="xl"
              title={<>When each <br className="hidden sm:block" /> variety peaks.</>}
              subtitle="Hover or tap a colored cell to highlight a variety’s peak month in South Florida’s mango calendar."
            />
          </Reveal>
          <Reveal>
            <div className="mt-10 sm:mt-12">
              <SeasonalCalendar tone="ink" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Filters */}
      <section className="block-cream border-b-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-10">
          <div data-testid="varieties-filter-tabs" className="flex flex-wrap gap-0 border-2 border-[hsl(var(--c-ink))] w-fit">
            {[
              { id: 'all', label: 'All Varieties', variant: 'ink' },
              { id: 'peak', label: 'Peak Now', variant: 'leaf' },
              { id: 'seasonal', label: 'In Season', variant: 'orange' },
              { id: 'limited', label: 'Limited', variant: 'red' },
            ].map((tab, i) => {
              const active = filter === tab.id;
              const variantBg = {
                ink: 'bg-[hsl(var(--c-ink))] text-white',
                leaf: 'bg-[hsl(var(--c-leaf))] text-white',
                orange: 'bg-[hsl(var(--c-mango-orange))] text-white',
                red: 'bg-[hsl(var(--c-mango-red))] text-white',
              }[tab.variant];
              return (
                <button
                  key={tab.id}
                  onClick={() => setFilter(tab.id)}
                  data-testid={`varieties-filter-${tab.id}`}
                  aria-pressed={active}
                  className={`btn-square btn-transition px-5 sm:px-7 h-12 text-xs sm:text-sm font-bold uppercase tracking-wider ${i > 0 ? 'border-l-2 border-[hsl(var(--c-ink))]' : ''} ${active ? variantBg : 'bg-white text-[hsl(var(--c-ink))] hover:bg-[hsl(var(--c-cream-deep))]'}`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section id="varieties-grid" data-testid="varieties-grid" className="block-cream">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-16 lg:py-24">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border-2 border-[hsl(var(--c-ink))]">
            {filtered.map((v, i) => (
              <Reveal key={v.slug} delay={i * 0.03}>
                <div
                  className="bg-white h-full flex flex-col border-r-2 border-b-2 border-[hsl(var(--c-ink))] last:border-r-0 sm:[&:nth-child(2n)]:border-r-0 lg:[&:nth-child(2n)]:border-r-2 lg:[&:nth-child(3n)]:border-r-0 lg:last:border-r-0 lift-on-hover"
                  data-testid={`variety-card-${v.slug}`}
                >
                  <div className={`relative aspect-[5/3] ${COLOR_BG[v.color]} flex items-center justify-center overflow-hidden border-b-2 border-[hsl(var(--c-ink))]`}>
                    <div className="absolute inset-0 grain" />
                    <MangoIllustration className="w-[50%] drop-shadow-[0_18px_30px_rgba(0,0,0,0.25)] float-y" />
                    <div className="absolute top-3 left-3">
                      <span className={`text-[10px] uppercase tracking-[0.16em] px-2.5 py-1.5 font-bold border-2 border-[hsl(var(--c-ink))] ${BADGE_BG[v.badge]}`}>{v.availability}</span>
                    </div>
                  </div>
                  <div className="p-6 sm:p-7 lg:p-8 flex-1 flex flex-col">
                    <h3 className="font-heading text-2xl sm:text-3xl font-extrabold leading-tight">{v.name}</h3>
                    <p className="mt-3 text-[hsl(var(--c-ink-soft))] leading-relaxed text-sm">{v.flavor}</p>
                    <div className="mt-5 sm:mt-6 space-y-3 text-sm">
                      <div className="flex items-start gap-2">
                        <Calendar className="h-4 w-4 mt-0.5 text-[hsl(var(--c-teal))] shrink-0" />
                        <div><span className="font-bold">Harvest:</span> <span className="text-[hsl(var(--c-ink-soft))]">{v.season}</span></div>
                      </div>
                      <div className="flex items-start gap-2">
                        <Sun className="h-4 w-4 mt-0.5 text-[hsl(var(--c-mango-orange))] shrink-0" />
                        <div><span className="font-bold">Ripening:</span> <span className="text-[hsl(var(--c-ink-soft))]">{v.ripening}</span></div>
                      </div>
                      <div className="flex items-start gap-2">
                        <Leaf className="h-4 w-4 mt-0.5 text-[hsl(var(--c-leaf))] shrink-0" />
                        <div><span className="font-bold">Florida-grown.</span> <span className="text-[hsl(var(--c-ink-soft))]">Tree-ripened by local growers.</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="block-orange border-t-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-20">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <SectionHeader tone="cream" eyebrow="Ready For Florida Flavor?" size="xl" title="Get a box. Or a truckload." />
            <div className="flex flex-wrap gap-0">
              <SquareButton to="/shop" variant="yellow" size="lg">Shop Mango Boxes</SquareButton>
              <SquareButton to="/wholesale" variant="ink" size="lg" className="-ml-[2px]">Wholesale Inquiry</SquareButton>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
